import React, { ReactNode } from 'react'
import { VideoExtensionRequestRecord } from '@/api/videoApi'
import { VirtualizedGrid } from '@/components/ui/common'

interface ExtensionRequestsGridProps {
  items: VideoExtensionRequestRecord[]
  columns: number
  renderItem: (req: VideoExtensionRequestRecord) => ReactNode
  getItemKey: (req: VideoExtensionRequestRecord) => string
  sentinelRef: React.RefObject<HTMLDivElement | null> | React.RefObject<HTMLDivElement>
  isLoading: boolean
  isFetchingNextPage: boolean
  isError: boolean
  allLoaded: boolean
  emptyIcon?: ReactNode
  emptyMessage?: string
  errorMessage?: string
}

const LoadingSkeleton = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
    {[1, 2, 3].map(i => (
      <div key={i} className="rounded-xl bg-gray-200 animate-pulse" style={{ aspectRatio: '16/9' }} />
    ))}
  </div>
)

const ExtensionRequestsGrid: React.FC<ExtensionRequestsGridProps> = ({
  items,
  columns,
  renderItem,
  getItemKey,
  sentinelRef,
  isLoading,
  isFetchingNextPage,
  isError,
  allLoaded,
  emptyIcon,
  emptyMessage = 'No extension requests yet',
  errorMessage = 'Failed to load extension requests',
}) => {
  return (
    <>
      {isError && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {errorMessage}
        </div>
      )}

      {isLoading && items.length === 0 && <LoadingSkeleton />}

      {!isLoading && items.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          {emptyIcon || (
            <svg className="w-10 h-10 mb-3 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.069A1 1 0 0121 8.882v6.236a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
            </svg>
          )}
          <p className="text-sm">{emptyMessage}</p>
        </div>
      )}

      {items.length > 0 && (
        <VirtualizedGrid
          items={items}
          columns={columns}
          estimatedRowHeight={260}
          emptyAspectRatio="16/9"
          renderItem={renderItem}
          getItemKey={getItemKey}
        />
      )}

      {/* Infinite scroll sentinel */}
      <div ref={sentinelRef} className="h-8 mt-4" />

      {isFetchingNextPage && <LoadingSkeleton />}

      {allLoaded && items.length > 0 && (
        <p className="text-center text-xs text-gray-400 mt-2 pb-4">All requests loaded</p>
      )}
    </>
  )
}

export default ExtensionRequestsGrid
